import type {Sorting} from '@igp/shared/api';
import {useAuthUser} from '@igp/shared/auth';
import {useAutoMapper} from '@igp/shared/auto-mapper';
import type {PayoutReport} from '@igp/shared/contract/pam';
import {extendedViewCleanDelay, type UseListViewEntityProps, type UseListViewEntityResult, useViewInit} from '@igp/shared/redux/view';
import {useColumnsSettings} from '@igp/shared/user-settings';
import {momentToTimestampSeconds} from '@igp/shared/utils';
import {EntityType} from '@models';

import {firstAvailableDate} from './constants';
import {payoutReportMappingKeys} from './mapper';
import type {
    PayoutReportColumns,
    PayoutReportColumnSettings,
    PayoutReportFilterKeys,
    PayoutReportQueryFields,
    PayoutReportSortKeys,
    PayoutReportViewModel,
    PayoutReportViewModelKeys,
} from './types';

export function usePayoutReport({
    viewType,
    fields,
    defaultFilters,
    defaultSorting,
    defaultPaging,
    syncWithUrl,
    validateFilter,
    cleanDelay = extendedViewCleanDelay,
    displayName,
}: UseListViewEntityProps<PayoutReportFilterKeys, PayoutReportViewModelKeys>): UseListViewEntityResult<
    PayoutReportViewModel,
    PayoutReportViewModelKeys
> {
    const mapper = useAutoMapper();
    const queryFields = fields.map(f =>
        mapper.map<PayoutReportViewModelKeys, PayoutReportQueryFields>(
            f,
            payoutReportMappingKeys.viewModelsKeys,
            payoutReportMappingKeys.queryItemsKeys
        )
    );

    const {
        items: serverItems,
        totalCount,
        viewEntity,
        getSearchFilter,
        handleSortChange,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
    } = useViewInit<EntityType, PayoutReport, PayoutReportFilterKeys, PayoutReportQueryFields, PayoutReportSortKeys>({
        viewType,
        entity: {entity: EntityType.PayoutReport, fields: queryFields},
        cleanDelay,
        defaultFilters,
        hiddenFilters: [{key: 'transaction_date_min', value: momentToTimestampSeconds(firstAvailableDate)}],
        defaultPaging,
        defaultSorting: mapSorting(defaultSorting ?? [{field: 'agent_username', sort: 'asc'}]),
        syncWithUrl,
        validateFilter,
        displayName,
    });

    const items = serverItems.map(i =>
        mapper.tryMap<PayoutReport, PayoutReportViewModel>(i, payoutReportMappingKeys.model, payoutReportMappingKeys.viewModel)
    );
    const searchFilter = getSearchFilter(i =>
        mapper.map<PayoutReportSortKeys, PayoutReportViewModelKeys>(
            i,
            payoutReportMappingKeys.sortKeys,
            payoutReportMappingKeys.viewModelsKeys
        )
    );

    function mapSorting(sorting: Sorting<PayoutReportViewModelKeys>[]): Sorting<PayoutReportSortKeys>[] {
        return (
            sorting?.map(s => ({
                field: mapper.map<PayoutReportViewModelKeys, PayoutReportSortKeys>(
                    s.field,
                    payoutReportMappingKeys.viewModelsKeys,
                    payoutReportMappingKeys.sortKeys
                ),
                sort: s.sort,
            })) ?? []
        );
    }

    function handleVmSortChange(sorting: Sorting<PayoutReportViewModelKeys>[]) {
        handleSortChange(mapSorting(sorting));
    }

    return {
        items: items?.map(i => i.model),
        rawItems: items?.map(i => ({isValid: i.isModelValid, raw: i.rawModelString})),
        totalCount,
        searchFilter,
        filterString: viewEntity?.filter,
        handleSortChange: handleVmSortChange,
        handleFilterChange,
        handlePageChange,
        handlePageSizeChange,
    };
}

type UsePayoutReportColumnSettingsProps = {
    gridId: string;
    defaultColumns: PayoutReportColumns[];
    pinnedColumns?: PayoutReportColumns[];
};

type UsePayoutReportColumnSettingsResult = {
    visibleColumns?: PayoutReportColumns[];
    onVisibleColumnsChange: (value: PayoutReportColumns[]) => void;
};

export function usePayoutReportColumnSettings({
    gridId,
    defaultColumns,
    pinnedColumns,
}: UsePayoutReportColumnSettingsProps): UsePayoutReportColumnSettingsResult {
    const {sub} = useAuthUser();
    const {visible, onVisibleColumnsChange} = useColumnsSettings<PayoutReportColumnSettings>({
        userId: sub,
        settingId: gridId,
        defaultColumns,
        pinnedColumns,
    });

    function handleColumnSettingsChange(columns: PayoutReportColumns[]): void {
        onVisibleColumnsChange(columns);
    }

    return {
        visibleColumns: visible,
        onVisibleColumnsChange: handleColumnSettingsChange,
    };
}
